import { useCallback, useEffect, useRef, useState } from 'react';

export type DineInMenuTheme = 'dark' | 'light';

const STORAGE_KEY = 'theories_dinein_menu_theme';
const FADE_MS = 480;

export type DineInMenuThemeTokens = {
  bg: string;
  text: string;
  muted: string;
  mutedSoft: string;
  faint: string;
  border: string;
  borderSoft: string;
  navFade: string;
  navBg: string;
  navBorder: string;
  navHighlight: string;
  navActive: string;
  navInactive: string;
  warn: string;
  errorBorder: string;
  errorBg: string;
  errorIcon: string;
};

export const DINEIN_THEME_COLORS = {
  darkBg: '#0a0a0a',
  lightBg: '#f6f5f2',
} as const;

const themes: Record<DineInMenuTheme, DineInMenuThemeTokens> = {
  dark: {
    bg: DINEIN_THEME_COLORS.darkBg,
    text: '#ffffff',
    muted: 'rgba(255,255,255,0.45)',
    mutedSoft: 'rgba(255,255,255,0.38)',
    faint: 'rgba(255,255,255,0.25)',
    border: 'rgba(255,255,255,0.1)',
    borderSoft: 'rgba(255,255,255,0.08)',
    navFade: 'linear-gradient(to top, #0a0a0a 55%, transparent)',
    navBg: 'rgba(18,18,18,0.92)',
    navBorder: 'rgba(255,255,255,0.12)',
    navHighlight: 'rgba(255,255,255,0.08)',
    navActive: '#ffffff',
    navInactive: 'rgba(255,255,255,0.35)',
    warn: 'rgba(255,200,120,0.75)',
    errorBorder: 'rgba(255,80,80,0.35)',
    errorBg: 'rgba(255,60,60,0.08)',
    errorIcon: '#f87171',
  },
  light: {
    bg: DINEIN_THEME_COLORS.lightBg,
    text: '#0a0a0a',
    muted: 'rgba(10,10,10,0.45)',
    mutedSoft: 'rgba(10,10,10,0.42)',
    faint: 'rgba(10,10,10,0.22)',
    border: 'rgba(10,10,10,0.12)',
    borderSoft: 'rgba(10,10,10,0.08)',
    navFade: 'linear-gradient(to top, #f6f5f2 55%, transparent)',
    navBg: 'rgba(255,255,255,0.92)',
    navBorder: 'rgba(10,10,10,0.1)',
    navHighlight: 'rgba(10,10,10,0.06)',
    navActive: '#0a0a0a',
    navInactive: 'rgba(10,10,10,0.35)',
    warn: '#9a6700',
    errorBorder: 'rgba(185,28,28,0.3)',
    errorBg: 'rgba(185,28,28,0.06)',
    errorIcon: '#dc2626',
  },
};

function readTheme(): DineInMenuTheme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* ignore */
  }
  return 'dark';
}

export function useDineInMenuTheme() {
  const [theme, setThemeState] = useState<DineInMenuTheme>(() =>
    typeof window === 'undefined' ? 'dark' : readTheme(),
  );
  /** Токены UI (текст/бордеры) меняем в середине fade фона */
  const [uiTheme, setUiTheme] = useState<DineInMenuTheme>(() =>
    typeof window === 'undefined' ? 'dark' : readTheme(),
  );
  const busyRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;

    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setUiTheme(next);
        busyRef.current = false;
      }, Math.round(FADE_MS * 0.45));
      return next;
    });
  }, []);

  const setTheme = useCallback((next: DineInMenuTheme) => {
    if (busyRef.current) return;
    setThemeState((prev) => {
      if (prev === next) return prev;
      busyRef.current = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setUiTheme(next);
        busyRef.current = false;
      }, Math.round(FADE_MS * 0.45));
      return next;
    });
  }, []);

  return {
    theme,
    uiTheme,
    isLight: uiTheme === 'light',
    tokens: themes[uiTheme],
    toggleTheme,
    setTheme,
    fadeMs: FADE_MS,
  };
}
